/**
 * The weekly device summary (tidaltech/koi#8361): what each run decided about
 * each camera system, its settings and snoozes. Backed by leatherback's
 * Notifications service. A run emails our own owners of each customer whose
 * watch-only date has passed (#8362); before that it only records who would
 * be told.
 */

export const deviceSituations = ['QUIET', 'NOT_INSTALLED', 'REPLACED'] as const;
export type DeviceSituation = (typeof deviceSituations)[number];

export const deviceMessages = [
  'PLEASE_RETURN',
  'CHECK_OFFLINE',
  'PLEASE_INSTALL',
  'INTERNAL_ONLY',
] as const;
export type DeviceMessage = (typeof deviceMessages)[number];

export const noticeDecisions = ['WOULD_SEND', 'SENT', 'SKIPPED', 'NOT_DUE', 'SEND_FAILED'] as const;
export type NoticeDecision = (typeof noticeDecisions)[number];

/** The device types in the summary: every type that reports heartbeats. */
export const deviceTypeLabels: Record<string, string> = {
  CAMERA_SYSTEM: 'Camera system',
  WINCH_SYSTEM: 'Winch system',
  UNIT_CONTROLLER: 'Unit controller',
  POWERBOX: 'Powerbox',
  SITE_CONTROLLER: 'Site controller',
};

export function deviceTypeLabel(type: string | undefined): string {
  return (type && deviceTypeLabels[type]) || 'Device';
}

export const deliveries = ['INTERNAL', 'CUSTOMER'] as const;
export type Delivery = (typeof deliveries)[number];

export const emailStatuses = ['SENT', 'FAILED', 'HELD'] as const;
export type EmailStatus = (typeof emailStatuses)[number];

export const emailStatusLabels: Record<EmailStatus, string> = {
  SENT: 'Sent',
  FAILED: 'Failed; tried again next run',
  HELD: 'Held: not on this server’s allowlist',
};

export const situationLabels: Record<DeviceSituation, string> = {
  QUIET: 'Quiet where it last reported',
  NOT_INSTALLED: 'Not reporting at its site',
  REPLACED: 'Replaced by another device',
};

export const messageLabels: Record<DeviceMessage, string> = {
  PLEASE_RETURN: 'Please return',
  CHECK_OFFLINE: 'Check it’s online',
  PLEASE_INSTALL: 'Please install',
  INTERNAL_ONLY: 'Can’t tell: Tidal only',
};

export const messageHints: Record<DeviceMessage, string> = {
  PLEASE_RETURN: 'Its pen has no fish',
  CHECK_OFFLINE: 'Its pen has fish',
  PLEASE_INSTALL: 'Moved or delivered here and not reporting',
  INTERNAL_ONLY: 'No fish data for its pen',
};

export const deliveryLabels: Record<Delivery, string> = {
  INTERNAL: 'Tidal’s Customer Success and Field Services owners',
  CUSTOMER: 'The customer’s contacts (not sent yet)',
};

export interface NotificationRun {
  id: string;
  createTime?: string | undefined;
  startedBy?: string | undefined;
  watchOnly: boolean;
  devices: number;
  included: number;
  skipped: number;
  notDue: number;
  emailsSent: number;
  emailsFailed: number;
}

/** One summary email a run sent, or tried to. */
export interface NotificationEmail {
  id: string;
  runId?: string | undefined;
  recipient: string;
  subject?: string | undefined;
  noticeIds: string[];
  status?: EmailStatus | undefined;
  error?: string | undefined;
  createTime?: string | undefined;
}

export interface DeviceNotice {
  id: string;
  runId?: string | undefined;
  itemId: string;
  customerId?: string | undefined;
  siteId?: string | undefined;
  situation?: DeviceSituation | undefined;
  message?: DeviceMessage | undefined;
  decision?: NoticeDecision | undefined;
  skipReason?: string | undefined;
  waitingSince?: string | undefined;
  daysWaiting: number;
  lastHeartbeatSiteId?: string | undefined;
  penId?: string | undefined;
  firstIncluded?: string | undefined;
  escalationLevel: number;
  recipients: string[];
  customerRecipients: string[];
  language?: string | undefined;
  createTime?: string | undefined;
  /** The item's part type, e.g. WINCH_SYSTEM. */
  deviceType?: string | undefined;
  /** For a replaced device: the item that took its site and pen. */
  replacedByItemId?: string | undefined;
}

export interface NotificationSettings {
  id?: string | undefined;
  customerId?: string | undefined;
  quietAfterDays?: number | undefined;
  neverInstalledAfterDays?: number | undefined;
  reminderAfterDays: number[];
  escalateAfterDays?: number | undefined;
  digestIntervalDays?: number | undefined;
  snoozeMaxDays?: number | undefined;
  signalWindowDays?: number | undefined;
  watchOnlyUntil?: string | undefined;
  delivery?: Delivery | undefined;
  version: number;
  lastModified?: string | undefined;
  lastModifiedBy?: string | undefined;
}

export interface NotificationSettingsList {
  defaults: NotificationSettings;
  overrides: NotificationSettings[];
}

export interface NotificationSnooze {
  id: string;
  itemId: string;
  customerId?: string | undefined;
  siteId?: string | undefined;
  until: string;
  reason: string;
  createdBy?: string | undefined;
  createTime?: string | undefined;
}

export interface SnoozeInput {
  itemId: string;
  until: string;
  reason: string;
}

/** The numeric settings, in the order the page shows them. */
export const settingFields = [
  'quietAfterDays',
  'neverInstalledAfterDays',
  'escalateAfterDays',
  'digestIntervalDays',
  'snoozeMaxDays',
  'signalWindowDays',
] as const;
export type SettingField = (typeof settingFields)[number];

export const settingLabels: Record<
  SettingField | 'reminderAfterDays',
  { label: string; hint: string }
> = {
  quietAfterDays: {
    label: 'Quiet after',
    hint: 'Days without data at the site it last reported from',
  },
  neverInstalledAfterDays: {
    label: 'Not installed after',
    hint: 'Days at a site without reporting there, from when it arrived',
  },
  reminderAfterDays: {
    label: 'Remind after',
    hint: 'Days after the first notice, comma-separated',
  },
  escalateAfterDays: {
    label: 'Escalate after',
    hint: 'Days after the first notice, then to the escalation contacts',
  },
  digestIntervalDays: { label: 'Summary every', hint: 'Days between summaries' },
  snoozeMaxDays: { label: 'Longest snooze', hint: 'Days ahead a camera can be snoozed' },
  signalWindowDays: {
    label: 'Acted-on window',
    hint: 'Days for a camera to report again or get a return booked',
  },
};

/** Proto snake_case names, for clear_mask. */
export const settingProtoNames: Record<
  SettingField | 'reminderAfterDays' | 'delivery' | 'watchOnlyUntil',
  string
> = {
  quietAfterDays: 'quiet_after_days',
  neverInstalledAfterDays: 'never_installed_after_days',
  reminderAfterDays: 'reminder_after_days',
  escalateAfterDays: 'escalate_after_days',
  digestIntervalDays: 'digest_interval_days',
  snoozeMaxDays: 'snooze_max_days',
  signalWindowDays: 'signal_window_days',
  delivery: 'delivery',
  watchOnlyUntil: 'watch_only_until',
};

export interface SettingsPatch {
  customerId?: string | undefined;
  version: number;
  values: Partial<Record<SettingField, number>> & {
    reminderAfterDays?: number[];
    delivery?: Delivery;
    watchOnlyUntil?: string;
  };
  /** Override fields to remove (customer overrides only). */
  clear: string[];
}

export type SettingsDraft = Record<SettingField, string> & {
  reminderAfterDays: string;
  delivery: '' | Delivery;
  watchOnlyUntil: string;
};

export function settingsDraft(s?: NotificationSettings): SettingsDraft {
  const num = (v: number | undefined) => (v === undefined ? '' : String(v));
  return {
    quietAfterDays: num(s?.quietAfterDays),
    neverInstalledAfterDays: num(s?.neverInstalledAfterDays),
    escalateAfterDays: num(s?.escalateAfterDays),
    digestIntervalDays: num(s?.digestIntervalDays),
    snoozeMaxDays: num(s?.snoozeMaxDays),
    signalWindowDays: num(s?.signalWindowDays),
    reminderAfterDays: (s?.reminderAfterDays ?? []).join(', '),
    delivery: s?.delivery ?? '',
    watchOnlyUntil: s?.watchOnlyUntil?.slice(0, 10) ?? '',
  };
}

/**
 * Checks a draft and turns it into a patch. For the defaults every number is
 * required; for a customer an empty field means "use the default" and is
 * cleared if it was overridden before.
 */
export function buildSettingsPatch(
  draft: SettingsDraft,
  current: NotificationSettings,
  customerId?: string,
): { patch?: SettingsPatch; errors: Partial<Record<keyof SettingsDraft, string>> } {
  const errors: Partial<Record<keyof SettingsDraft, string>> = {};
  const patch: SettingsPatch = { version: current.version, values: {}, clear: [] };
  if (customerId) patch.customerId = customerId;
  for (const field of settingFields) {
    const raw = draft[field].trim();
    if (!raw) {
      if (!customerId) errors[field] = 'Enter a number of days.';
      else if (current[field] !== undefined) patch.clear.push(settingProtoNames[field]);
      continue;
    }
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      errors[field] = 'Use whole days from 1 to 365.';
      continue;
    }
    patch.values[field] = n;
  }
  const reminders = draft.reminderAfterDays.trim();
  if (reminders) {
    const days = reminders
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    const ok =
      days.every((d) => Number.isInteger(d) && d >= 1 && d <= 365) &&
      days.every((d, i) => i === 0 || d > days[i - 1]!);
    if (!ok) errors.reminderAfterDays = 'Use increasing whole days, e.g. 35, 49.';
    else patch.values.reminderAfterDays = days;
  } else if (customerId && current.reminderAfterDays.length > 0) {
    patch.clear.push(settingProtoNames.reminderAfterDays);
  }
  if (draft.delivery) patch.values.delivery = draft.delivery;
  else if (customerId && current.delivery) patch.clear.push(settingProtoNames.delivery);
  if (draft.watchOnlyUntil) patch.values.watchOnlyUntil = `${draft.watchOnlyUntil}T00:00:00Z`;
  // Emptied: watch-only again (the defaults can clear this one too).
  else if (current.watchOnlyUntil) patch.clear.push(settingProtoNames.watchOnlyUntil);
  return Object.keys(errors).length > 0 ? { errors } : { patch, errors };
}

/** What a notice's decision means, in words. */
export function decisionLabel(
  n: Pick<
    DeviceNotice,
    'decision' | 'escalationLevel' | 'firstIncluded' | 'createTime' | 'skipReason'
  >,
): string {
  switch (n.decision) {
    case 'WOULD_SEND':
    case 'SENT': {
      const verb = n.decision === 'SENT' ? 'Sent' : 'Would send';
      if (n.escalationLevel > 0) return `${verb}: escalated`;
      if (!n.firstIncluded || n.firstIncluded === n.createTime) return `${verb}: new`;
      // A first email can follow weeks of watch-only runs, so say since when
      // rather than guess whether it's a reminder.
      return `${verb}: flagged since ${n.firstIncluded.slice(0, 10)}`;
    }
    case 'SKIPPED':
      return `Left out: ${n.skipReason ?? ''}`.trim();
    case 'NOT_DUE':
      return 'Already told; no reminder due';
    case 'SEND_FAILED':
      return 'Email failed; tried again next run';
    default:
      return '';
  }
}

export function waitingLabel(n: Pick<DeviceNotice, 'situation' | 'daysWaiting'>): string {
  const days = `${n.daysWaiting} ${n.daysWaiting === 1 ? 'day' : 'days'}`;
  if (n.situation === 'REPLACED') return `${days} since it was replaced`;
  return n.situation === 'NOT_INSTALLED'
    ? `${days} since it arrived`
    : `${days} since its last data`;
}

export const NO_OWNER = '';

export interface RecipientGroup {
  recipient: string;
  notices: DeviceNotice[];
}

/**
 * Notices grouped by who the summary goes to (one email each), biggest
 * first; notices with nobody to tell come last.
 */
export function groupByRecipient(notices: readonly DeviceNotice[]): RecipientGroup[] {
  const groups = new Map<string, DeviceNotice[]>();
  for (const n of notices) {
    const keys = n.recipients.length > 0 ? n.recipients : [NO_OWNER];
    for (const key of keys) groups.set(key, [...(groups.get(key) ?? []), n]);
  }
  return [...groups.entries()]
    .map(([recipient, list]) => ({
      recipient,
      notices: [...list].sort(
        (a, b) => (a.siteId ?? '').localeCompare(b.siteId ?? '') || b.daysWaiting - a.daysWaiting,
      ),
    }))
    .sort(
      (a, b) =>
        Number(a.recipient === NO_OWNER) - Number(b.recipient === NO_OWNER) ||
        b.notices.length - a.notices.length ||
        a.recipient.localeCompare(b.recipient),
    );
}

export type DecisionFilter = 'INCLUDED' | 'SKIPPED' | 'NOT_DUE' | 'ALL';

export function matchesDecision(n: DeviceNotice, filter: DecisionFilter): boolean {
  switch (filter) {
    case 'INCLUDED':
      return n.decision === 'WOULD_SEND' || n.decision === 'SENT' || n.decision === 'SEND_FAILED';
    case 'SKIPPED':
      return n.decision === 'SKIPPED';
    case 'NOT_DUE':
      return n.decision === 'NOT_DUE';
    default:
      return true;
  }
}

/** A device's heartbeats from one site and pen within a month (tidaltech/koi#8447). */
export interface PenActivity {
  deviceId: string;
  siteId: string;
  /** The pen's name. */
  pen: string;
  /** YYYY-MM-DD. */
  firstDay: string;
  lastDay: string;
  days: number;
}

export interface PenActivityMonth {
  /** YYYY-MM. */
  month: string;
  /** The last day with heartbeats so far; absent when the month has none. */
  throughDay?: string | undefined;
  activity: PenActivity[];
}
